import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { fetchLogs, getFilters, getLogs } from "../api/logs.api";
import type {
  Incident,
  IncidentStatus,
  IncidentType,
  Log,
  LogFilters,
} from "../types/log.types";
import {
  LOG_STATUSES,
  LOG_TYPES,
  LOG_SERVICES,
  FILTER_OPTIONS,
} from "../constants/log.constants";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { toast } from "sonner";
import { DatePickerWithPresets } from "@/shared/components/range-picker/RangePicker";

export function LogsPage() {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [filters, setFilters] = useState<LogFilters>({
    eventType: "all",
    service: "all",
    status: "all",
    dateFrom: "",
    dateTo: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    loadIncidents();
  }, [filters, currentPage]);

  const loadIncidents = async () => {
    try {
      setIsLoading(true);

      const res = await getLogs({
        status: filters.status === "all" ? "" : filters.status,
        type: filters.eventType === "all" ? "" : filters.eventType,
        service: filters.service === "all" ? "" : filters.service,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      });

      // res: IncidentsResponse
      setIncidents(res.data);
      setTotal(res.total);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Не удалось добавить пользователя";
      toast.error(message);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (key: keyof LogFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      eventType: "all",
      service: "all",
      status: "all",
      dateFrom: "",
      dateTo: "",
    });
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  };

  const totalPages = Math.ceil(total / pageSize);
  const displayedCount = Math.min(currentPage * pageSize, total);

  const INCIDENT_TYPES: Record<IncidentType, string> = {
    API: "API",
    PAYMENT_ERROR: "Ошибка оплаты",
    API_ERROR: "Ошибка API",
  };

  const INCIDENT_SERVICES: Record<string, string> = {
    push: "Push",
    payment: "Платежи",
    airline: "Авиакомпания",
  };

  const INCIDENT_STATUSES: Record<
    IncidentStatus,
    {
      label: string;
      variant: "primary" | "success" | "info" | "warning" | "danger" | "muted";
    }
  > = {
    new: { label: "Новый", variant: "info" },
    in_progress: { label: "В работе", variant: "warning" },
    resolved: { label: "Решён", variant: "success" },
    closed: { label: "Закрыт", variant: "muted" },
  };
  const handleDateChange = (dateFrom: string, dateTo: string) => {
    setFilters((prev) => ({ ...prev, dateFrom, dateTo, page: 1 }));
  };
  const formatIncidentDate = (iso: string) =>
    new Date(iso).toLocaleString("ru-RU");

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="px-6 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Логи и ошибки</h1>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 flex-wrap">
                <DatePickerWithPresets
                  dateFrom={filters.dateFrom}
                  dateTo={filters.dateTo}
                  onChange={handleDateChange}
                />
                <Select
                  value={filters.eventType}
                  onValueChange={(value) =>
                    handleFilterChange("eventType", value)
                  }
                >
                  <SelectTrigger className="w-[180px] h-9 text-sm bg-gray-50 border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILTER_OPTIONS.eventTypes.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.service}
                  onValueChange={(value) =>
                    handleFilterChange("service", value)
                  }
                >
                  <SelectTrigger className="w-[160px] h-9 text-sm bg-gray-50 border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILTER_OPTIONS.services.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.status}
                  onValueChange={(value) => handleFilterChange("status", value)}
                >
                  <SelectTrigger className="w-[140px] h-9 text-sm bg-gray-50 border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILTER_OPTIONS.statuses.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetFilters}
                  className="h-9 text-sm text-gray-600 hover:text-gray-900"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Сброс фильтров
                </Button>
              </div>

              <div className="text-sm text-gray-500">
                Показано {displayedCount} из {total}
              </div>
            </div>

            {/* Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 hover:bg-gray-50">
                    <TableHead className="text-xs font-semibold text-gray-600 uppercase">
                      Дата
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-gray-600 uppercase">
                      Тип
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-gray-600 uppercase">
                      Сервис
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-gray-600 uppercase">
                      Описание
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-gray-600 uppercase">
                      Связанный объект
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-gray-600 uppercase">
                      Статус
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-8 text-gray-500"
                      >
                        Загрузка...
                      </TableCell>
                    </TableRow>
                  ) : incidents.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-8 text-gray-500"
                      >
                        Инциденты не найдены
                      </TableCell>
                    </TableRow>
                  ) : (
                    incidents.map((incident) => (
                      <TableRow key={incident.id} className="hover:bg-gray-50">
                        <TableCell className="text-sm text-gray-900 font-medium">
                          {formatIncidentDate(incident.createdAt)}
                        </TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {INCIDENT_TYPES[incident.type]}
                        </TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {INCIDENT_SERVICES[incident.service] ??
                            incident.service}
                        </TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {incident.description}
                        </TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {incident.relatedObject || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={INCIDENT_STATUSES[incident.status].variant}
                          >
                            {INCIDENT_STATUSES[incident.status].label}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="h-8 w-8"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="h-8 w-8"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
